import { SharedDealService } from "@js/services/Deal/SharedDealService";
import {
  AccountClient,
  AddressLookupDTO,
  ApplicationUserCrudClient,
  AppraisalModuleEnum,
  BridgingDeal,
  CaseAccessLevelEnum,
  CommercialDeal,
  DealClient,
  DealLender,
  DealLenderClient,
  DevelopmentFinanceDeal,
  LicenseMasterStatusEnum,
  LocationEnum,
  PostalAddress,
  ProductFamilyEnum,
  ProductTypeEnum,
  StreetAddressClient,
} from "@src/apis/ModularClient";
import { IndexService } from "@js/services/IndexService";
import { SelectListService } from "@js/services/SelectListService";
import { getOneUsingOData } from "@ccq/ts-lib";
import { RoleService } from "@js/services/RoleService";
import type { EditorSettings } from "tinymce";
import angular from "angular";

export abstract class BaseCaseController<T extends BridgingDeal | CommercialDeal | DevelopmentFinanceDeal> {
  dealDto: T;
  dealId: number;
  dealForm: ng.IFormController;
  messageContent: string;
  showRequestEmailForm: boolean = false;
  sendingMessage: boolean = false;
  appName: string;
  caseStatusOptions = [];
  requestMessageReason: string = "";
  shareNote: string;
  shareContext: AppraisalModuleEnum = null;
  phoneNumber: string;
  dataLoading: boolean;
  isAdmin: boolean = false;
  isLender: boolean = false;
  isBroker: boolean = false;
  isClient: boolean = false;
  isOrganisationAdmin: boolean = false;
  isBrickflow: boolean = false;
  dealLender: DealLender;
  caseStatusText: string = "";
  isConnectUser: boolean;
  isInActiveLender: boolean = false;
  currentUserFullName: string = "";
  //property for roles directive to handle referred readonly view for broker/connect broker
  isReadOnlyDeal: boolean = true;

  //overview properties
  caseOverview: string = "";
  tabChangePending: number = 0;
  showUnsavedChangesModal: boolean = false;
  pendingTabCallback = null;
  isCaseOverviewFocused: boolean = false;
  unsavedKind: string = "";
  unsavedPromptText: string = "";
  isOverviewFocused: boolean = false;
  suppressNextBlurPrompt: boolean = false;
  isHandlingLocationChange: boolean = false;
  skipNextLocationChange: boolean = false;

  isRoleEvaluationComplete: boolean = false;
  isInvitedAndHasReadonlyAccess: boolean = true;
  requestAssistancePhoneNumber: string;

  suggestions: AddressLookupDTO[] = [];
  containerId: string | null = null;
  selectedAddress: any = null;

  //Autosave
  saveTimer: any;
  skipAutosave: boolean;
  excludeAutoSaveFields = [];
  pendingChanges = {};

  howPostcodeErrorMessage: boolean = false;
  previouslySelectedLocation: LocationEnum;
  showPostcodeErrorMessage: boolean;
  postcodeErrorMsg: string;
  showSuggestions: boolean = false;
  CaseDashboardTypeEnum = {
    Overview: 0,
    Borrower: 1,
    Property: 2,
    Loan: 3,
  };

  debounceTimer: any;
  dealAddress: string;
  showConfirmGenerateSummaryModal: boolean = false;
  isGenerating: boolean = false;
  tinymceReadonlyOptions: EditorSettings;
  tinymceNoteOptions: EditorSettings;
  tinymceOptions: EditorSettings;
  tinymceOverviewOptions: EditorSettings;
  isDrillingDown = false;

  static $inject = [
    "SharedDealService",
    "IndexService",
    "AccountClient",
    "DealClient",
    "SelectListService",
    "$location",
    "$rootScope",
    "RoleService",
    "ApplicationUserCrudClient",
    "DealLenderClient",
    "$timeout",
    "StreetAddressClient",
    "$window",
    "$scope",
  ];

  constructor(
    protected sharedDealService: SharedDealService,
    protected indexService: IndexService,
    protected accountClient: AccountClient,
    protected dealClient: DealClient,
    protected selectListService: SelectListService,
    protected $location: ng.ILocationService,
    protected $rootScope: ng.IRootScopeService,
    protected roleService: RoleService,
    protected applicationUserCrudClient: ApplicationUserCrudClient,
    protected dealLenderClient: DealLenderClient,
    protected $timeout: ng.ITimeoutService,
    protected streetAddressClient: StreetAddressClient,
    protected $window: ng.IWindowService,
    protected $scope: ng.IScope,
  ) {
    $rootScope.$on("$locationChangeStart", (event, newUrl) => {
      const newPath = newUrl.split("#!")[1] || "/";

      if (this.isHandlingLocationChange) return;

      const overviewDirty = this.caseOverview !== this.dealDto.projectDescription;

      if (overviewDirty) {
        event.preventDefault();

        this.pendingTabCallback = () => {
          this.isHandlingLocationChange = true;

          this.$location.path(newPath);

          this.$timeout(() => {
            this.isHandlingLocationChange = false;
          }, 100);
        };

        this.setUnsavedChangePrompt("caseOverview");

        return;
      }

      this.isHandlingLocationChange = true;
      this.$timeout(() => {
        this.$location.path(newPath);
      }, 0);
    });

    this.tinymceOptions = this.sharedDealService.tinymceOptions;
    this.tinymceReadonlyOptions = angular.copy(this.tinymceOptions);
    this.tinymceReadonlyOptions.readonly = true;
    this.tinymceReadonlyOptions.min_height = 200;
    this.tinymceReadonlyOptions.max_height = 600;
    this.tinymceReadonlyOptions.height = "47vh";
    this.initTinyEditors();
  }

  constructShareNoteMessage() {
    this.shareNote = this.sharedDealService.constructShareNoteMessage();
  }

  async updateRequestAssitanceDealNoteMessage() {
    const userId = await this.indexService.currentUserId;
    if (this.dealDto?.dealNotes != null) {
      this.dealDto.dealNotes = this.dealDto.dealNotes.map((d) => {
        if (
          d.userId != userId &&
          (d.noteText == "Your have requested for assistance." || d.noteText == "You have requested assistance.")
        ) {
          return { ...d, NoteText: `${d.user.fullName} requested assistance.` };
        }

        return d;
      });
    }
  }

  sendRequestEmail() {
    this.showRequestEmailForm = false;
    this.sendingMessage = true;
    this.dataLoading = true;

    this.accountClient
      .requestAssistanceReview(
        this.requestMessageReason,
        this.dealDto.id,
        this.dealDto.productFamily,
        this.requestAssistancePhoneNumber,
      )
      .then(() => {
        this.showMessage(
          `${this.appName} have been notified that you require assistance and will be in touch shortly to help.`,
        );
      })
      .catch(() => {
        this.showMessage("Sorry, something went wrong while sending your email. Please try again.");
      })
      .finally(() => {
        this.sendingMessage = false;
        this.dataLoading = false;
        this.requestAssistancePhoneNumber = this.phoneNumber;
      });
  }

  showMessage(message: string) {
    this.messageContent = message;
  }

  async checkIfUserIsInvited() {
    //added to scope for directive
    const response = await this.dealClient.isUserInvitedToDeal({
      userId: await this.indexService.currentUserId,
      dealId: this.dealId,
    });

    const licenceData = await this.indexService.currentUserLicenseData;
    const subscriptionStatus = licenceData.subscriptionStatus;

    // if user has no active subscription (and are not a lender or admin) then they should be redirected back to the dashboard
    if (
      subscriptionStatus != LicenseMasterStatusEnum.PaidUp &&
      subscriptionStatus != LicenseMasterStatusEnum.PreCancel &&
      subscriptionStatus != LicenseMasterStatusEnum.PaymentProcessing &&
      !response.isInvited &&
      !this.isAdmin &&
      !this.isLender
    ) {
      this.$location.path("/dashboard");
      return;
    }

    (this.$rootScope as any).isInvitedToCase = response.isInvited;

    if (response.isInvited) {
      if (response.dealAccessLevel == CaseAccessLevelEnum.ReadOnly) {
        (this.$rootScope as any).isInvitedAndHasReadonlyAccess = true;
      } else {
        this.isInvitedAndHasReadonlyAccess = false; // show lendershortlist & resuts button to clients.
      }
    } else {
      this.isInvitedAndHasReadonlyAccess = false; // show lendershortlist & resuts button to non-client users.
    }

    this.$rootScope.$broadcast("invitedStatusChange");
  }

  generateCaseStatusText() {
    if (this.isLender) {
      this.caseStatusOptions = this.selectListService.GetCaseLenderStateOptionsForLenders();
    } else {
      this.caseStatusOptions = this.selectListService.GetCaseStatusOptions(this.isConnectUser);
    }
    this.getCaseStatusDisplayText();
  }

  /**Gets the Case Status display text */
  getCaseStatusDisplayText() {
    let statusItem = null;

    if (this.isLender) {
      statusItem = this.caseStatusOptions.find((statusOption) => this.dealLender.status == statusOption.key);
    } else {
      statusItem = this.caseStatusOptions.find((statusOption) => this.dealDto.status == statusOption.key);
    }

    this.caseStatusText = statusItem.displayName;
  }

  showEditDealNameButton() {
    return !this.isReadOnlyDeal && (!this.isConnectUser || (this.isConnectUser && !this.dealDto.isReferredToConnect));
  }

  async getCurrentUserAndRoles() {
    [this.isClient, this.isAdmin, this.isBroker, this.isLender, this.isOrganisationAdmin] = await Promise.all([
      this.roleService.isClientUser(),
      this.roleService.isAdminUser(),
      this.roleService.isBroker(),
      this.roleService.isLenderUser(),
      this.roleService.isOrganiationAdminUser(),
    ]);

    this.isRoleEvaluationComplete = true;
    if (this.isLender) {
      this.dealLenderClient.fetchDealLender(this.dealDto.id).then((response) => {
        this.dealLender = response;
        this.isInActiveLender = response.isLenderInActive;
        this.generateCaseStatusText();
      });
    }

    const { phoneNumber, isConnectUser, firstName, lastName, organisation } = await getOneUsingOData(
      this.applicationUserCrudClient,
      {
        filter: { id: { eq: await this.indexService.currentUserId } },
        select: {
          phoneNumber: true,
          isConnectUser: true,
          firstName: true,
          lastName: true,
          organisation: true,
        },
        expand: {
          organisation: {
            select: {
              isBrickflow: true,
            },
          },
        },
      },
    );

    this.phoneNumber = this.requestAssistancePhoneNumber = phoneNumber;
    this.isConnectUser = isConnectUser;
    this.currentUserFullName = `${firstName} ${lastName}`;
    this.isBrickflow = organisation?.isBrickflow ?? false;
    this.checkIfUserIsInvited();
    //this.updateRequestAssitanceDealNoteMessage();
    if (!this.isLender) this.generateCaseStatusText();
  }

  goToTab(label, callback) {
    const overviewDirty = this.caseOverview !== this.dealDto.projectDescription;

    let kind = null;
    if (overviewDirty) {
      kind = "caseOverview";
    }

    if (kind) {
      this.tabChangePending = label;
      this.pendingTabCallback = callback;
      this.setUnsavedChangePrompt(kind);
    } else {
      callback.call(this);
    }
  }

  confirmTabChangeWithoutSave() {
    if (this.unsavedKind === "caseOverview") {
      this.caseOverview = this.dealDto.projectDescription;
    }

    if (this.pendingTabCallback) {
      this.pendingTabCallback.call(this);
    }
    this.resetUnsavedChangePrompt();
  }

  cancelTabChange() {
    this.resetUnsavedChangePrompt();
  }

  resetUnsavedChangePrompt() {
    this.showUnsavedChangesModal = false;
    this.tabChangePending = null;
    this.pendingTabCallback = null;
    this.unsavedKind = null;
    this.unsavedPromptText = null;
  }

  onOverviewFocus() {
    this.isOverviewFocused = true;
  }

  handleOverviewBlur() {
    if (this.suppressNextBlurPrompt) {
      this.suppressNextBlurPrompt = false;
      return;
    }
    this.$timeout(() => {
      const isDirty = this.caseOverview !== this.dealDto.projectDescription;
      if (isDirty && !this.showUnsavedChangesModal) {
        this.tabChangePending = null;
        this.pendingTabCallback = null;
        this.setUnsavedChangePrompt("caseOverview");
      }
      this.isOverviewFocused = false;
    }, 120);
  }

  setUnsavedChangePrompt(kind: "caseOverview") {
    this.unsavedKind = kind;
    this.unsavedPromptText =
      kind === "caseOverview"
        ? "Do you want to save your Case Overview changes?"
        : "Do you want to save your Private Case Notes changes?";

    this.showUnsavedChangesModal = true;
  }

  scrollTo(id) {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  onAddressChange() {
    if (!this.dealAddress) {
      this.suggestions = [];
      return;
    }

    if (this.dealAddress.length >= 3) {
      this.streetAddressClient.getAddressList(this.dealAddress).then((response) => {
        this.suggestions = response || [];
        this.showSuggestions = true;
      });
    }
  }

  onSelectingAddressOption($event: MouseEvent, item: AddressLookupDTO) {
    // Prevent the input from losing focus and triggering blur handlers
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }

    if (item.type.toLowerCase() !== "address") {
      // Drill down into container
      this.containerId = item.id;
      this.dealAddress = item.addressLine1;
      this.streetAddressClient
        .getPostCodeAddressList(this.containerId, this.dealAddress)
        .then((response) => {
          this.suggestions = response || [];
        })
        .finally(() => {
          this.isDrillingDown = false;
        });
    } else {
      // Final address → retrieve full details
      this.retrieveAddress(item.id);
    }
  }

  selectBorrowerTab() {
    this.goToTab(1, function () {
      this.showAppraisalPermission = false;
      this.selectedNavMenu = 1;
      this.scrollTo("borrowerSection");
    });
  }

  selectOverviewTab() {
    this.goToTab(0, function () {
      this.selectedNavMenu = 0;
      this.showAppraisalPermission = false;
      this.scrollTo("topSection");
    });
  }

  selectDocumentTab() {
    this.goToTab(5, function () {
      this.showAppraisalPermission = false;
      this.selectedNavMenu = 5;
      this.scrollTo("caseDocumentSection");
    });
  }

  closeAddressDropDown(event) {
    event.stopPropagation();
    if (this.isDrillingDown) return;
    const target = event.target as HTMLElement | null;
    if (!target) return;

    // If the click was inside ANY address-lookup instance, ignore it
    if (target.closest('[data-address-lookup]')) return;

    this.showSuggestions = false;
  }

  setSuggestionsValue() {
    event.stopPropagation();
    this.showSuggestions = !this.showSuggestions;
  }

  populateAddressField() {
    if (
      this.dealDto.propertyAddress == null ||
      ((this.dealDto.propertyAddress.postCode == null || this.dealDto.propertyAddress.postCode.length <= 0) &&
        (this.dealDto.propertyAddress.addressLine1 == null || this.dealDto.propertyAddress.addressLine1.length <= 0))
    ) {
      this.dealAddress = this.dealDto.postcodeSearchString;
    }
  }

  getAddressAsString(address: PostalAddress) {
    return this.sharedDealService.getAddressAsString(address);
  }

  protected abstract flushAutosave();
  showBorrowerSection() {
    if (this.dealDto && this.dealDto.productFamily != ProductFamilyEnum.Bridging) return true;

    if (
      this.dealDto &&
      this.dealDto.productType != ProductTypeEnum.BridgingPurchaseOrRefinance &&
      this.dealDto.productType != ProductTypeEnum.BridgingRefurb
    )
      return true;
  }

  generateSummary() {
    this.isGenerating = true;
    this.showConfirmGenerateSummaryModal = false;
    this.dealClient
      .createSummary(this.dealId, this.caseOverview)
      .then((response) => {
        this.caseOverview = response;
      })
      .finally(() => {
        this.isGenerating = false;
      });
  }

  confirmGenerateSummary() {
    if (this.caseOverview && this.caseOverview.length > 0) {
      this.showConfirmGenerateSummaryModal = true;
    } else {
      this.generateSummary();
    }
  }

  hasGenerateAccess() {
    return this.isAdmin || (this.isOrganisationAdmin && this.isBrickflow);
  }

  initTinyEditors() {
    this.tinymceOverviewOptions = angular.copy(this.tinymceOptions);
    this.tinymceOverviewOptions.height = undefined;
    this.tinymceOverviewOptions.setup = (editor) => {
      editor.on("focus", () => {
        this.$scope.$evalAsync(() => {
          this.onOverviewFocus();
        });
      });
      editor.on("blur", () => {
        this.$scope.$evalAsync(() => {
          this.handleOverviewBlur();
        });
      });
    };
  }

  onPurchaseDateChange() {
    if (
      this.dealDto.originalPurchaseDate &&
      !this.dealForm.originalPurchaseDate.$error.max &&
      !this.dealForm.originalPurchaseDate.$error.min
    )
      this.scheduleAutosave("originalPurchaseDate", this.dealDto.originalPurchaseDate, null);
  }

  scheduleAmountAutosave(fieldName: string, value: any, fieldCtrl: ng.INgModelController) {
    if (fieldCtrl && !fieldCtrl.$valid) return;

    if (value == "" || isNaN(value)) {
      return;
    }

    this.scheduleAutosave(fieldName, value, fieldCtrl);
  }

  // Schedule autosave with debounce
  scheduleAutosave(fieldName: string, value: any, fieldCtrl: ng.INgModelController | null) {
    if (fieldCtrl && !fieldCtrl.$valid) return;

    if (Object.keys(this.pendingChanges).length == 0) {
      this.pendingChanges = { id: this.dealDto.id };
    }

    this.pendingChanges[fieldName] = value;

    // Debounce: wait 1s after last change
    this.$timeout.cancel(this.saveTimer);
    this.saveTimer = this.$timeout(() => this.flushAutosave(), 1000);
  }

  retrieveAddress(id: string) {
    this.streetAddressClient.getFullAddress(id).then((address) => {
      this.dealDto.propertyAddress.addressLine1 = address.addressLine1;
      this.dealDto.propertyAddress.addressLine2 = address.addressLine2;
      this.dealDto.propertyAddress.addressLine3 = address.addressLine3;
      this.dealDto.propertyAddress.addressLine4 = address.addressLine4;
      this.dealDto.propertyAddress.postCode = address.postCode;
      this.dealAddress = "";
      this.scheduleAutosave("propertyAddress", this.dealDto.propertyAddress, null);
      this.showSuggestions = false;
      this.suggestions = [];
    });
  }

  showShareButton(shareholder: DealClient) {
    if (shareholder.accessLevel == CaseAccessLevelEnum.Hidden) {
      return false;
    }

    // If the shareholder has already been verified then don't show the button
    if (shareholder.isVerified) {
      return true;
    }

    // At least 2 of the security questions should be answered
    var count = 0;

    if (shareholder.client.phoneNumber) {
      count++;
    }

    if (shareholder.client.postalAddress.postCode) {
      count++;
    }

    if (shareholder.client.dateOfBirth) {
      count++;
    }

    if (count >= 2) {
      return true;
    }

    return false;
  }

  onAddressOutsideClick(e: MouseEvent) {
    if (this.isDrillingDown) return;
    this.showSuggestions = false;
  }

  getRequiredRolesOptionsForResults() {
    return `[{name: 'Broker', disable: ${this.isReadOnlyDeal}, hide: false}]`;
  }
}
